import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import { SupplierInvoiceSaving } from '@supoui/shared/model/supplier-invoice-saving';
import { SupplierInvoiceItem } from '@supoui/shared/model/supplier-invoice-item';
import { DropdownDictionary } from '@supoui/shared/model/dropdownDictionary';
import { IServiceOrder } from '@supoui/shared/model/service-order';
import { IServiceItem } from '@supoui/shared/model/service-item';
import { SavingTypedInField } from '@supoui/shared/model/enumerations/saving-typed-in-field.enum';
import { QuantityUnit } from '@supoui/shared/model/enumerations/quantity-unit.enum';
import { DictionaryService } from '@supoui/core/services/dictionary.service';
import { AbstractFormGroupComponent } from '@supoui/shared/components/abstract-form-group.component';
import { CaseStakeholder } from '@supoui/shared/model/caseStakeholder';
import { ServiceTypeEnum } from '@supoui/shared/model/enumerations/service-type.enum';
import { DropDownServiceOrderItemModel } from './drop-down-service-order-item.model';
import { NgxCurrencyInputMode } from 'ngx-currency';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';

@Component({
    selector: 'app-supplier-invoice-item',
    templateUrl: './supplier-invoice-item.component.html',
    styleUrls: ['./supplier-invoice-item.component.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, multi: true, useExisting: SupplierInvoiceItemComponent },
        { provide: NG_VALIDATORS, multi: true, useExisting: SupplierInvoiceItemComponent }
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SupplierInvoiceItemComponent extends AbstractFormGroupComponent implements OnInit, ControlValueAccessor, Validator {
  public static readonly ITEM_OUTPATIENT_TREATMENT_CODE = 'SI112';

  @Input() showDelete: boolean;
  @Input() localCurrency: string;
  @Input() localAmount: number | null;
  @Input() isSupplierMedicalEscort: boolean;
  @Input() hideSavingButtons: boolean;
  @Input() isBatchBilling: boolean;
  @Input() isBatchBillingActive: boolean;

  _caseCreatedDate: Date = new Date();
  @Input() set caseCreatedDate(date: Date) {
    if (date != null) {
      this._caseCreatedDate = date;
      this.allServiceItems = this.filterServiceItems(this.allServiceItems, this.caseCreatedDate);
    }
  }

  get caseCreatedDate(): Date {
    return this._caseCreatedDate;
  }

  @Input() set serviceOrders(value: IServiceOrder[]) {
    this._serviceOrders = value;
    this.setServiceOrderItems();
    if (this.invoiceItem && this.invoiceItem.serviceOrderId) {
      this.serviceOrderChange(this.invoiceItem.serviceOrderId, false);
    }
  }

  get serviceOrders(): IServiceOrder[] {
    return this._serviceOrders;
  }

  @Input() set caseStakeholders(value: CaseStakeholder[]) {
    this.setServiceOrderItems();
    this._caseStakeholders = value;
  }
  get caseStakeholders(): CaseStakeholder[] {
    return this._caseStakeholders;
  }

  @Input() set isSupplierPartner(value: boolean) {
    this._isSupplierPartner = value;
    this.fetchReductionList();
  }
  get isSupplierPartner(): boolean {
    return this._isSupplierPartner;
  }

  @Output() delete: EventEmitter<SupplierInvoiceItem> = new EventEmitter();
  @Output() serviceOrderChangeEvent: EventEmitter<any> = new EventEmitter();

  serviceItemList: DropdownDictionary[];
  quantityUnits: DropdownDictionary[];
  reductionList: DropdownDictionary[];
  serviceOrderItems: DropDownServiceOrderItemModel[] = [];
  selectableServiceOrders: IServiceOrder[];

  get grossSavings(): UntypedFormArray {
    return this.fg.controls['grossSavings'] as UntypedFormArray;
  }
  get netSavings(): UntypedFormArray {
    return this.fg.controls['netSavings'] as UntypedFormArray;
  }

  disabled: boolean = false;
  private invoiceItem: SupplierInvoiceItem;

  // @ts-ignore
  onChange = invoiceItem => {};
  onTouched = () => {};

  private _serviceOrders: IServiceOrder[];
  private _caseStakeholders: CaseStakeholder[];
  private allServiceItems: IServiceItem[];
  private _isSupplierPartner: boolean;
  private isCalculatingSaving = false;
  private staticServiceOrdersLoaded = false;
  private staticServiceOrders: DropDownServiceOrderItemModel[] = [];
  private previousServiceOrderType: ServiceTypeEnum | undefined;
  private isWritingValue = false;

  currencyMaskOptions = {
    prefix: '',
    thousands: '.',
    decimal: ',',
    precision: 2,
    allowNegative: true,
    inputMode: NgxCurrencyInputMode.Natural
  };

  constructor(
    private dictionaryService: DictionaryService,
    private translate: TranslateService,
    private fb: UntypedFormBuilder,
    private logger: NGXLogger
  ) {
    super();
  }

  ngOnInit(): void {
    this.fg = this.fb.group({
      serviceOrderId: [{ value: null, disabled: this.disabled }, [Validators.required]],
      serviceOrderType: [{ value: null }],
      serviceItemCode: [{ value: null, disabled: this.disabled }, [Validators.required]],
      serviceItemName: [{ value: null }],
      quantity: [
        { value: null, disabled: this.disabled },
        [Validators.required, Validators.min(0), Validators.pattern(/^\d*[.]?\d{0,2}$/)]
      ],
      quantityUnit: [{ value: null, disabled: this.disabled }, [Validators.required]],
      paidAmount: [{ value: this.localAmount, disabled: this.disabled }, [Validators.required, Validators.pattern(/^-?\d*[.]?\d{0,2}$/)]],
      savings: this.fb.array([]),
      grossSavings: this.fb.array([]),
      netSavings: this.fb.array([]),
      isIncludingVAT: [{ value: false }],
      calculatedFromGross: [{ value: false }]
    });
    this.subscriptions.push(
      this.fg.valueChanges.subscribe(() => this.markAsChanged()),
      this.fg.controls.serviceOrderId.valueChanges.subscribe(newServiceOrderId => {
        if (this.invoiceItem && newServiceOrderId) {
          this.invoiceItem.serviceOrderId = newServiceOrderId;
        }
        this.serviceOrderChange(newServiceOrderId, true);
      }),
      this.fg.controls.serviceItemCode.valueChanges.subscribe(newServiceItemCode => {
        const newServiceItem = this.allServiceItems?.find(s => s.serviceItemCode === newServiceItemCode);
        this.fg.controls.serviceItemName.setValue(newServiceItem?.serviceItemName);
        this.fg.controls.isIncludingVAT.setValue(newServiceItem?.vat);
      }),
      this.fg.controls.paidAmount.valueChanges.subscribe(newPaidAmount => {
        if (!this.isCalculatingSaving && newPaidAmount && !this.disabled) {
          this.isCalculatingSaving = true;
          if (!this.fg.controls.paidAmount.disabled) {
            this.fg.controls.calculatedFromGross.setValue(false);
            this.grossSavings.controls.forEach(saving => saving.get('totalAmount')?.disable());
          }
          this.netSavings.controls.forEach(saving => {
            if (saving.value.typedInField === SavingTypedInField.AMOUNT) {
              saving
                .get('percentage')
                ?.setValue(
                  Math.round(
                    (10000 * saving.value.amount) / this.getTotalAmountSavingLineNumber(saving.value.lineNumber - 1, saving.value.gross)
                  ) / 100
                );
            } else {
              saving
                .get('amount')
                ?.setValue(
                  Math.round(
                    saving.value.percentage * this.getTotalAmountSavingLineNumber(saving.value.lineNumber - 1, saving.value.gross)
                  ) / 100
                );
            }
          });
          this.isCalculatingSaving = false;
        }
      })
    );

    this.serviceItemList = [];

    this.quantityUnits = Object.values(QuantityUnit).map(
      unit => new DropdownDictionary(unit, this.translate.instant('QUANTITY_UNIT_' + unit))
    );

    this.setServiceOrderItems();
    this.setServiceItems();
    this.fetchReductionList();
  }

  focusElement(event: any) {
    event.target.select();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const requiredItem = ['serviceOrderId', 'serviceItemCode', 'quantity', 'quantityUnit'];
    const requiredSaving = ['reductionTypeCode', 'amount', 'percentage'];
    if (!this.fg.valid) {
      let itemValid = true;
      requiredItem.forEach(attr => (itemValid = itemValid && !this.fg.get(attr)?.invalid));
      let savingsValid = true;
      for (let i = 0; i < this.getAllSavings().length; i++) {
        requiredSaving.forEach(attr => (savingsValid = savingsValid && !this.getAllSavings().controls[i].get(attr)?.invalid));
      }
      if (!itemValid || !savingsValid) {
        return { supplierInvoiceItem: {} };
      }
      if (this.fg.controls.calculatedFromGross.value) {
        const totalAmountValid = this.grossSavings.length > 0 ? !this.grossSavings.controls[0].get('totalAmount')?.invalid : false;
        if (!totalAmountValid) {
          return { supplierInvoiceItem: {} };
        }
      } else {
        if (this.fg.controls.paidAmount.enabled && !this.fg.controls.paidAmount.valid) {
          return { supplierInvoiceItem: {} };
        }
      }
    }
    return null;
  }

  private filterServiceItems(serviceItems: IServiceItem[], caseCreatedDate: Date): IServiceItem[] {
    if (serviceItems?.length > 0 && caseCreatedDate) {
      serviceItems = serviceItems.filter(
        si => si.activateOn && si.activateOn <= this.caseCreatedDate && (!si.deactivateOn || si.deactivateOn > this.caseCreatedDate)
      );
    }
    if (serviceItems?.length && !this.isBatchBilling && !this.isBatchBillingActive) {
      serviceItems = serviceItems.filter(si => si.serviceItemCode !== SupplierInvoiceItemComponent.ITEM_OUTPATIENT_TREATMENT_CODE);
    }
    return serviceItems;
  }

  private fetchReductionList() {
    this.subscriptions.push(
      this.dictionaryService.getReductions().subscribe(reductions => {
        this.reductionList = reductions
          .filter(
            reduction =>
              reduction.usedInSupplierInvoice &&
              reduction.active &&
              (!reduction.partnerOnly || (this._isSupplierPartner && reduction.partnerOnly))
          )
          .map(reduction => new DropdownDictionary(reduction.reductionCode, reduction.reductionName));
      })
    );
  }

  private setServiceOrderItems() {
    let caseServiceOrders: DropDownServiceOrderItemModel[] = [];

    if (!this.staticServiceOrdersLoaded) {
      this.subscriptions.push(
        this.dictionaryService.getServiceTypes().subscribe(serviceTypes => {
          this.staticServiceOrders = serviceTypes
            .filter(st => st.activateOn <= this.caseCreatedDate && (!st.deactivateOn || st.deactivateOn > this.caseCreatedDate))
            .map(
              serviceType =>
                new DropDownServiceOrderItemModel({
                  serviceOrderId: serviceType.serviceTypeCode,
                  serviceOrderType: serviceType.serviceTypeEnumValue,
                  serviceTypeName: serviceType.serviceTypeName,
                  isServiceOrder: false
                })
            );
          this.staticServiceOrdersLoaded = true;
          caseServiceOrders = this.serviceOrderItems.filter(s => s.isServiceOrder);
          caseServiceOrders.forEach(so => {
            const serviceTypeName = this.staticServiceOrders.find(s => s.serviceOrderType === so.serviceOrderType)?.serviceTypeName;
            if (serviceTypeName) {
              so.serviceTypeName = serviceTypeName;
            }
          });
          if (this.isBatchBilling) {
            this.setBatchBillingSelectedServiceOrder();
          }
          this.publishServiceOrderItems(caseServiceOrders);
        })
      );
    }
    if (this.serviceOrders && this.caseStakeholders) {
      this.serviceOrders.forEach(so => {
        const supplier = this.caseStakeholders.find(stakeholder => +stakeholder.id === so.supplierId);
        const serviceTypeName = this.staticServiceOrders.find(s => s.serviceOrderType === so.type)?.serviceTypeName;
        caseServiceOrders.push(
          new DropDownServiceOrderItemModel({
            serviceOrderId: so.serviceOrderId,
            serviceOrderType: so.type,
            serviceTypeName,
            isServiceOrder: true,
            providerName: supplier?.companyName
          })
        );
      });
      this.publishServiceOrderItems(caseServiceOrders);
    }
  }

  private setServiceItems(): void {
    this.subscriptions.push(
      this.dictionaryService.getServiceItems().subscribe(serviceItems => {
        this.allServiceItems = this.filterServiceItems(serviceItems, this.caseCreatedDate);
        if (this.invoiceItem && this.invoiceItem.serviceOrderId) {
          this.serviceOrderChange(this.invoiceItem?.serviceOrderId, false);
        }
        if (this.isBatchBilling) {
          this.setBatchBillingSelectedServiceItem();
        }
      })
    );
  }

  private publishServiceOrderItems(caseServiceOrders: DropDownServiceOrderItemModel[]) {
    const allServiceOrders: DropDownServiceOrderItemModel[] = [];
    allServiceOrders.push(...caseServiceOrders, ...this.staticServiceOrders);

    if (this.isSupplierMedicalEscort) {
      this.serviceOrderItems = caseServiceOrders.filter(s => s.serviceOrderType === ServiceTypeEnum.MEDICAL_ESCORT);
    } else if (this.isBatchBilling) {
      this.serviceOrderItems = this.staticServiceOrders.filter(s => s.serviceOrderType === ServiceTypeEnum.TREATMENT);
    } else {
      this.serviceOrderItems = allServiceOrders;
    }

    if (this.invoiceItem?.serviceOrderId) {
      this.serviceOrderChange(this.invoiceItem?.serviceOrderId, true);
    }
  }

  private markAsTouched() {
    if (this.fg.touched) {
      this.onTouched();
    }
  }

  private markAsChanged() {
    const amended = new SupplierInvoiceItem(this.fg.getRawValue());
    amended.id = this.invoiceItem?.id;
    this.onChange(amended);
    setTimeout(() => this.markAsTouched(), 0);
  }

  private setBatchBillingSelectedServiceOrder(): void {
    if (this.isBatchBilling) {
      const treatmentServiceOrderItem = this.staticServiceOrders.find(s => s.serviceOrderType === ServiceTypeEnum.TREATMENT);
      if (treatmentServiceOrderItem) {
        this.fg.controls.serviceOrderId.patchValue(treatmentServiceOrderItem.serviceOrderId);
        this.fg.controls.serviceOrderType.setValue(treatmentServiceOrderItem.serviceOrderType);
        this.fg.controls.serviceOrderId.disable();
      }
    }
  }

  private setBatchBillingSelectedServiceItem(): void {
    if (this.isBatchBilling) {
      this.serviceItemList = this.allServiceItems
        .filter(sii => sii.serviceItemCode === SupplierInvoiceItemComponent.ITEM_OUTPATIENT_TREATMENT_CODE)
        .map(sii => new DropdownDictionary(sii.serviceItemCode, sii.serviceItemName));
      if (this.serviceItemList.length > 0) {
        this.fg.controls.serviceItemCode.patchValue(this.serviceItemList[0].id);
        this.fg.controls.serviceItemCode.disable();
      }
    }
  }

  private serviceOrderChange(serviceOrderId: string, reset: boolean): void {
    if (!this.allServiceItems || this.isBatchBilling) {
      return;
    }

    if (!serviceOrderId || this.fg.disabled) {
      this.serviceItemList = this.allServiceItems.map(sii => new DropdownDictionary(sii.serviceItemCode, sii.serviceItemName));
      return;
    }

    let serviceOrderItem: IServiceOrder | undefined;
    let newServiceOrderType: ServiceTypeEnum | undefined;
    if (this.serviceOrders) {
      serviceOrderItem = this.serviceOrders.find(soi => soi.serviceOrderId === serviceOrderId);
    }
    if (serviceOrderItem && serviceOrderItem.type) {
      newServiceOrderType = serviceOrderItem.type;
      this.serviceItemList = this.allServiceItems
        .filter(s => s.serviceTypes.find(st => st.serviceTypeEnumValue == serviceOrderItem?.type))
        .map(sii => new DropdownDictionary(sii.serviceItemCode, sii.serviceItemName));
    } else {
      const soType = this.staticServiceOrders.find(so => so.serviceOrderId === serviceOrderId)?.serviceOrderType;
      if (soType) {
        newServiceOrderType = ServiceTypeEnum[soType];
      }
      this.serviceItemList = this.allServiceItems
        .filter(s => s.serviceTypes.find(st => st.serviceTypeCode === serviceOrderId))
        .map(sii => new DropdownDictionary(sii.serviceItemCode, sii.serviceItemName));
    }

    if (reset && newServiceOrderType !== this.previousServiceOrderType) {
      this.previousServiceOrderType = newServiceOrderType;
      this.fg.controls.serviceItemCode.setValue(null);
      this.fg.controls.serviceOrderType.setValue(newServiceOrderType);

      setTimeout(() => {
        this.fg.controls.serviceOrderType.setValue(newServiceOrderType);
        this.serviceOrderChangeEvent.emit({ serviceOrderId, serviceOrderType: newServiceOrderType });
      }, 0);
    }
  }

  getSavingsAmountTotal(): number {
    let savingsSum: number = 0;
    this.getAllSavings()
      .value.map((saving: SupplierInvoiceSaving) => saving.amount)
      .forEach((amount: number) => (savingsSum += +amount));
    return savingsSum;
  }

  getSavingsPctTotal(): number {
    let grossAmount = +this.getPaidAmount();
    let savingsSum: number = 0;
    this.getAllSavings().value.forEach((saving: SupplierInvoiceSaving) => {
      savingsSum += +saving.amount;
      if (saving.gross) {
        grossAmount += +saving.amount;
      }
    });
    return Math.round((savingsSum * 10000) / grossAmount) / 100;
  }

  getNetAmount(): number {
    let netSavingsSum: number = +this.getPaidAmount();
    this.getAllSavings()
      .value.filter((saving: SupplierInvoiceSaving) => !saving.gross)
      .map((saving: SupplierInvoiceSaving) => saving.amount)
      .forEach((amount: number) => (netSavingsSum -= +amount));

    return netSavingsSum;
  }

  deleteSpecification(): void {
    this.delete.emit(this.invoiceItem);
  }

  addSaving(isGross: boolean, saving?: SupplierInvoiceSaving): UntypedFormGroup {
    this.isCalculatingSaving = true;
    const allSavings = this.getAllSavings();
    let lineNumber = 0;
    for (let i = 0; i < allSavings.length; i++) {
      if (allSavings.controls[i] && allSavings.controls[i].value.lineNumber >= lineNumber) {
        lineNumber = 1 + allSavings.controls[i].value.lineNumber;
      }
    }
    const newSaving = this.fb.group({
      id: [saving?.id],
      reductionTypeCode: [saving?.reductionTypeCode, Validators.required],
      lineNumber,
      amount: [saving?.amount, [Validators.required, Validators.pattern(/^-?\d*[.]?\d{0,2}$/)]],
      percentage: [saving?.percentage, [Validators.required, Validators.pattern(/^-?\d*[.]?\d{0,2}$/), Validators.max(100)]],
      totalAmount: [saving?.totalAmount, [Validators.required, Validators.pattern(/^-?\d*[.]?\d{0,2}$/)]],
      typedInField: [!!saving ? saving.typedInField : SavingTypedInField.AMOUNT],
      gross: isGross
    });
    if (isGross) {
      if (!!this.fg.value.paidAmount) {
        newSaving.controls.totalAmount.disable();
      }
      this.grossSavings.controls.forEach(saving => saving.get('totalAmount')?.disable());
      this.grossSavings.insert(0, newSaving);
    } else {
      this.netSavings.push(newSaving);
    }
    this.getAllSavings().push(newSaving);

    this.setSavingSubscriptions(newSaving);

    this.isCalculatingSaving = false;

    return newSaving;
  }

  private setSavingSubscriptions(newSaving: UntypedFormGroup) {
    const isGross = newSaving.value.gross;

    this.subscriptions.push(
      newSaving.controls.amount.valueChanges.subscribe(newAmount => {
        if (!this.isCalculatingSaving && newAmount && !this.disabled) {
          this.isCalculatingSaving = true;
          if (!this.isWritingValue) {
            newSaving.controls.typedInField.patchValue(SavingTypedInField.AMOUNT);
          }
          let newPercentage: number;
          if (!isGross || !this.fg.value.calculatedFromGross) {
            const netAmountBeforeSaving = this.getTotalAmountSavingLineNumber(newSaving.value.lineNumber - 1, isGross);
            if (isGross) {
              const newGross = +newAmount + +netAmountBeforeSaving;
              newPercentage = Math.round((10000 * +newAmount) / newGross) / 100;
              newSaving.controls.totalAmount.setValue(+netAmountBeforeSaving + +newAmount);
            } else {
              newPercentage = Math.round((10000 * +newAmount) / +netAmountBeforeSaving) / 100;
            }
            newSaving.controls.percentage.setValue(newPercentage);
          } else {
            const newPercentage = Math.round((10000 * +newSaving.value.amount) / +newSaving.value.totalAmount) / 100;
            newSaving.controls.percentage.setValue(newPercentage);
            this.aggregateGrossSavings();
          }

          this.isCalculatingSaving = false;
        }
      }),
      newSaving.controls.percentage.valueChanges.subscribe(newPercentage => {
        if (!this.isCalculatingSaving && newPercentage && !this.disabled) {
          this.isCalculatingSaving = true;
          if (!this.isWritingValue) {
            newSaving.controls.typedInField.patchValue(SavingTypedInField.PERCENTAGE);
          }
          let newAmount: number;
          if (!isGross || !this.fg.value.calculatedFromGross) {
            const netAmountBeforeSaving = this.getTotalAmountSavingLineNumber(newSaving.value.lineNumber - 1, isGross);
            if (isGross) {
              newAmount = (Math.round((10000 * netAmountBeforeSaving) / (100 - +newPercentage)) - 100 * netAmountBeforeSaving) / 100;
              newSaving.controls.totalAmount.setValue(+netAmountBeforeSaving + +newAmount);
            } else {
              newAmount = Math.round(+newPercentage * netAmountBeforeSaving) / 100;
            }
            newSaving.controls.amount.setValue(newAmount);
          } else {
            const newAmount = Math.round(+newSaving.value.percentage * +newSaving.value.totalAmount) / 100;
            newSaving.controls.amount.setValue(newAmount);
            this.aggregateGrossSavings();
          }
          this.isCalculatingSaving = false;
        }
      }),
      newSaving.controls.totalAmount.valueChanges.subscribe(newGrossTotal => {
        if (!this.isCalculatingSaving && newGrossTotal && !this.disabled) {
          this.isCalculatingSaving = true;

          if (!this.isWritingValue) {
            this.fg.controls.paidAmount.disable();
            this.fg.controls.calculatedFromGross.setValue(true);
          }
          if (newSaving.value.typedInField === SavingTypedInField.AMOUNT) {
            const newPercentage = Math.round((10000 * +newSaving.value.amount) / +newGrossTotal) / 100;
            newSaving.controls.percentage.setValue(newPercentage);
          } else {
            const newAmount = Math.round(+newSaving.value.percentage * +newGrossTotal) / 100;
            newSaving.controls.amount.setValue(newAmount);
          }
          this.aggregateGrossSavings();

          this.isCalculatingSaving = false;
        }
      })
    );
  }

  private aggregateGrossSavings() {
    let totalAmount: number = 0;
    for (let i = 0; i < this.grossSavings.length; i++) {
      const savingLine = this.grossSavings.controls[i] as UntypedFormGroup;

      if (i === 0) {
        totalAmount = +savingLine.value.totalAmount - +savingLine.value.amount;
      } else {
        savingLine.controls.totalAmount.setValue(totalAmount);
        if (savingLine.value.typedInField === SavingTypedInField.AMOUNT) {
          const newPercentage: number = Math.round((10000 * +savingLine.value.amount) / totalAmount) / 100;
          savingLine.controls.percentage.setValue(newPercentage);
          totalAmount -= +savingLine.value.amount;
        } else {
          const amount = Math.round(+savingLine.value.percentage * totalAmount) / 100;
          savingLine.controls.amount.setValue(amount);
          totalAmount -= +amount;
        }
      }
    }
    if (this.fg.value.calculatedFromGross) {
      this.fg.controls.paidAmount.patchValue(totalAmount);
    }
  }

  private getAllSavings(): UntypedFormArray {
    return this.fg.controls['savings'] as UntypedFormArray;
  }

  removeSaving(saving: AbstractControl, gross: boolean): void {
    let lineNumber = saving.value.lineNumber;
    if (gross) {
      if (this.fg.controls.calculatedFromGross.value) {
        const grossTotalLine = this.getAllSavings().value.sort(
          (s1: SupplierInvoiceSaving, s2: SupplierInvoiceSaving) => s2.lineNumber - s1.lineNumber
        )[0].lineNumber;
        if (lineNumber === grossTotalLine) {
          this.fg.controls.calculatedFromGross.setValue(false);
          this.fg.controls.paidAmount.enable();
        }
      }
      this.removeSavingFromArray(this.grossSavings, lineNumber);
      this.aggregateGrossSavings();
    } else {
      this.removeSavingFromArray(this.netSavings, lineNumber);
    }
    this.removeSavingFromArray(this.getAllSavings(), lineNumber);
  }

  private removeSavingFromArray(savingsArray: UntypedFormArray, lineNumber: number) {
    let i = 0;
    while (i < savingsArray.length) {
      if (savingsArray.controls[i].value.lineNumber === lineNumber) {
        savingsArray.removeAt(i);
        i = savingsArray.length;
      }
      i++;
    }
  }

  private getTotalAmountSavingLineNumber(lineNumber: number, gross: boolean): number {
    const itemValue = this.fg.value;
    let paidAmount = this.getPaidAmount();
    const sign = gross ? +1 : -1;
    const ofType = itemValue.savings?.filter((s: SupplierInvoiceSaving) => s.gross === gross);
    if (ofType) {
      for (let saving of ofType) {
        if (+saving.lineNumber > +lineNumber) {
          return paidAmount;
        }
        paidAmount = +paidAmount + +sign * +saving.amount;
      }
    }
    return paidAmount;
  }

  private getPaidAmount(): number {
    return this.fg.controls.paidAmount.value;
  }

  getTotalAmountSaving(saving: AbstractControl): number {
    const lineNumber = saving.value.lineNumber;
    const gross = saving.value.gross;
    let amount = this.getPaidAmount();

    if (gross) {
      let i = this.grossSavings.length;
      while (--i >= 0) {
        const control = this.grossSavings.controls[i];
        if (control && control.value.lineNumber <= lineNumber && control.value.amount) {
          amount += +control.value.amount;
        }
      }
    } else {
      let nLine = -1;
      let i = -1;
      while (nLine < lineNumber && ++i < this.netSavings.length) {
        const control = this.netSavings.controls[i];
        nLine = control.value.lineNumber;
        amount -= control.value.amount ? +control.value.amount : 0;
      }
    }

    return amount;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.dictionaryService
        .getAllReductions()
        .subscribe(
          reductions =>
            (this.reductionList = reductions.map(reduction => new DropdownDictionary(reduction.reductionCode, reduction.reductionName)))
        );
      this.fg.disable();
    } else {
      this.fetchReductionList();
    }
  }

  writeValue(invoiceItem: SupplierInvoiceItem): void {
    this.isWritingValue = true;
    this.invoiceItem = invoiceItem;

    invoiceItem.savings?.forEach(saving => this.addSaving(saving.gross, saving));
    if (invoiceItem.calculatedFromGross) {
      this.fg.controls.paidAmount.disable();
      this.grossSavings.controls[0].get('totalAmount')?.enable();
    }

    this.fg.patchValue(invoiceItem);

    if (this.allServiceItems && this.allServiceItems.length > 0 && this.invoiceItem.serviceOrderId) {
      this.serviceOrderChange(this.invoiceItem.serviceOrderId, false);
    }
    this.isWritingValue = false;
  }

  validationError(control: AbstractControl | null): boolean {
    if (control) {
      return control.invalid && control.touched;
    }
    return false;
  }
}
